<template>
  <div>
    <WorkbookPageContent :page="page" />
    <ClientOnly>
      <UContainer>
        <div v-if="!ready || totalPcs === 0" class="text-center py-12" v-motion-slide-visible-once-bottom :delay="500">
          <h2 class="text-xl font-bold mb-2">Your cart is empty</h2>
          <p class="mb-4">You must have at least one item in your cart to place an order.</p>
          <UButton to="/cart" color="primary" variant="solid">Go to cart</UButton>
        </div>
        <div v-else>
          <UContainer class="my-8">
            <div class="md:flex gap-4">
              <div>
                <h2 class="text-lg font-bold mb-4">Account name</h2>
                <UInput class="w-full md:w-100 mb-4" v-model="accountName" variant="ghost"
                  :class="{ 'border border-red-500': accountName === '' }" placeholder="Enter your account name"
                  size="md" :disabled="isPlacingOrder" />
                <h2 class="text-lg font-bold mb-4">PO#</h2>
                <UInput class="w-full md:w-100 mb-4" v-model="poNumber" variant="ghost"
                  :class="{ 'border border-red-500': poNumber === '' }" placeholder="Enter PO number" size="md"
                  :disabled="isPlacingOrder" />
              </div>
              <div>
                <h2 class="text-lg font-bold mb-4">Shipping Address</h2>
                <UTextarea class="w-full md:w-100" v-model="shippingAddress" variant="ghost"
                  :class="{ 'border border-red-500': shippingAddress === '' }" placeholder="Enter shipping address"
                  size="md" :disabled="isPlacingOrder" :rows="3" />
              </div>
            </div>
            <div class="flex gap-4 mt-4">
              <div>
                <h2 class="text-lg font-bold mb-4">Requested Ship Date</h2>
                <UInput v-model="requestedShipDate" type="date" placeholder="DD/MM/YYYY" size="md"
                  :disabled="isPlacingOrder" />
              </div>
            </div>
          </UContainer>
          <UContainer class="mb-4">
            <h2 class="text-lg font-bold mb-4">Order summary</h2>
            <OrderSummaryTable :rows="sortedDisplayRows" :currency="currency" />
          </UContainer>
          <div class="flex justify-end gap-4 my-8">
            <UPopover v-model:open="draftPopoverOpen">
              <UButton color="neutral" variant="outline" size="md" :disabled="isPlacingOrder || !canPlaceOrder"
                :loading="isPlacingOrder" @click="openDraftPopover">
                Save as draft
              </UButton>
              <template #content>
                <div class="p-4">
                  <h3 class="text-center mb-2 font-bold">Save as Draft</h3>
                  <p class="text-sm mb-4 text-center">
                    This will save your order as a draft without submitting it.
                  </p>
                  <div class="flex gap-2 justify-center">
                    <UButton size="sm" color="primary" :loading="isPlacingOrder" :disabled="isPlacingOrder"
                      @click="confirmDraftOrder">
                      Yes, save draft
                    </UButton>
                    <UButton size="sm" variant="outline" @click="closeDraftPopover">
                      No
                    </UButton>
                  </div>
                </div>
              </template>
            </UPopover>
            <UPopover v-model:open="confirmPopoverOpen">
              <UButton
                color="primary"
                variant="solid"
                size="md"
                :disabled="confirmButtonDisabled"
                :loading="isPlacingOrder"
                :to="confirmButtonTo"
                @click="handleConfirmButtonClick"
              >
                {{ confirmButtonLabel }}
              </UButton>
              <template #content>
                <div class="p-4">
                  <h3 class="text-center mb-2 font-bold">Are you sure?</h3>
                  <p class="text-sm mb-4 text-center">
                    This will submit your order and clear your cart.
                  </p>
                  <div class="flex gap-2 justify-center">
                    <UButton size="sm" color="primary" :loading="isPlacingOrder" :disabled="isPlacingOrder"
                      @click="confirmOrder">
                      Yes, place order
                    </UButton>
                    <UButton size="sm" variant="outline" @click="closeConfirmPopover">
                      No
                    </UButton>
                  </div>
                </div>
              </template>
            </UPopover>
          </div>
        </div>
      </UContainer>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useCartStore } from '@/store/cart'
import { useCatalogStore } from '@/store/catalog'
import { useSettingsStore } from '@/store/settings'
import { useOrdersStore } from '@/store/orders'
import { useAuthStore } from '@/store/auth'
import { useRouter, useRoute } from 'vue-router'
import { formatNumber } from '@/utils/formatNumber'
import { normalizeSizeCode, sizeSortIndex } from '@/utils/sizes'
import WorkbookPageContent from '@/components/workbook/PageContent.vue'
import OrderSummaryTable from '@/components/OrderSummaryTable.vue'
import { usePlausible } from '@/composables/usePlausible'

const route = useRoute()


const { data: page } = await useAsyncData(route.path, () => {
  return queryCollection('pages').path(route.path).first()
})
const cart = useCartStore()
const catalog = useCatalogStore()
const settings = useSettingsStore()
const ordersStore = useOrdersStore()
const auth = useAuthStore()
const router = useRouter()
const toast = useToast()
const { trackRevenue, trackEvent } = usePlausible()
const isPlacingOrder = ref(false)
const confirmPopoverOpen = ref(false)
const draftPopoverOpen = ref(false)
const accountName = ref('')
const shippingAddress = ref('')
const requestedShipDate = ref('')
const poNumber = ref('')

onMounted(() => {
  cart.hydrate()
  settings.hydrate()
  ordersStore.hydrate()

  // Förifyll accountName med senaste orderns accountName om finns
  const latestOrder = ordersStore.latestOrder(auth.user?.email || '')
  if (latestOrder?.accountName) accountName.value = latestOrder.accountName
  if (latestOrder?.shippingAddress) shippingAddress.value = latestOrder.shippingAddress
  if (latestOrder?.requestedShipDate) requestedShipDate.value = latestOrder.requestedShipDate
})

const currency = computed(() => settings.currency)
const ready = computed(() => cart.hydrated && catalog.items.length > 0)
const totalPcs = computed(() => Object.values(cart.lineItems).reduce((sum, li) => sum + li.qty, 0))
const isAuthenticated = computed(() => auth.isAuthenticated)
const canPlaceOrder = computed(() =>
  !!accountName.value.trim() &&
  !!poNumber.value.trim() &&
  !!shippingAddress.value.trim() &&
  !!requestedShipDate.value &&
  totalPcs.value > 0
)
const canConfirmOrder = computed(() => canPlaceOrder.value && isAuthenticated.value)
const confirmButtonLabel = computed(() => isAuthenticated.value ? 'Confirm order' : 'Log in to confirm')
const loginRedirectUrl = computed(() => `/login?redirect=${encodeURIComponent(route.fullPath)}`)
const confirmButtonDisabled = computed(() =>
  isPlacingOrder.value || (isAuthenticated.value && !canPlaceOrder.value)
)
const confirmButtonTo = computed(() => (isAuthenticated.value ? undefined : loginRedirectUrl.value))

const savedModels = computed(() =>
  cart.savedStylesArr
    .map(st => catalog.modelByStyle(st))
    .filter((m) => !!m)
)

function modelHasQty(model) {
  return model.variants.some(v => {
    const li = cart.lineItems[v.SKU]
    return li && li.qty > 0
  })
}
const modelsWithQty = computed(() => savedModels.value.filter(modelHasQty))

function wsFieldForCurrency(cur: string): string {
  switch (cur) {
    case 'USD': return 'WSUSD'
    case 'EUR': return 'WSEUR'
    case 'GBP': return 'WSGBP'
    case 'CHF': return 'WSCHF'
    case 'CAD':
    default:
      return 'WSCAD'
  }
}

const displayRows = computed(() => {
  const rows = []
  for (const model of modelsWithQty.value) {
    for (const v of model.variants) {
      const li = cart.lineItems[v.SKU]
      if (li && li.qty > 0) {
        const priceField = wsFieldForCurrency(currency.value)
        const price = Number(v[priceField] ?? model[priceField] ?? 0)
        rows.push({
          modelName: model.name,
          style: v.Style,
          colour: v.Colour,
          size: normalizeSizeCode(v.SizeCode ?? v.Size ?? ''),
          sku: v.SKU,
          qty: li.qty,
          totalPrice: price * li.qty
        })
      }
    }
  }
  return rows
})
const sortedDisplayRows = computed(() => {
  return [...displayRows.value].sort((a, b) => {
    if (a.modelName !== b.modelName) return String(a.modelName).localeCompare(String(b.modelName))
    if (a.style !== b.style) return String(a.style).localeCompare(String(b.style))
    if (a.colour !== b.colour) return String(a.colour).localeCompare(String(b.colour))
    return sizeSortIndex(a.size) - sizeSortIndex(b.size)
  })
})
const orderTotal = computed(() => sortedDisplayRows.value.reduce((sum, row) => sum + row.totalPrice, 0))
const orderTotalCAD = computed(() => {
  const rows = []
  for (const model of modelsWithQty.value) {
    for (const v of model.variants) {
      const li = cart.lineItems[v.SKU]
      if (li && li.qty > 0) {
        const priceField = wsFieldForCurrency('CAD')
        const price = Number(v[priceField] ?? model[priceField] ?? 0)
        rows.push({
          totalPrice: price * li.qty
        })
      }
    }
  }
  return rows.reduce((sum, row) => sum + row.totalPrice, 0)
})

function openConfirmPopover() {
  if (!canConfirmOrder.value) return
  confirmPopoverOpen.value = true
}
function handleConfirmButtonClick() {
  if (!isAuthenticated.value) return
  openConfirmPopover()
}
function closeConfirmPopover() { confirmPopoverOpen.value = false }

function openDraftPopover() { draftPopoverOpen.value = true }
function closeDraftPopover() { draftPopoverOpen.value = false }

async function confirmDraftOrder() {
  closeDraftPopover()
  if (isPlacingOrder.value) return
  isPlacingOrder.value = true
  try {
    const lines = Object.values(cart.lineItems)
      .filter(li => li.qty > 0)
      .map(li => {
        const variant = catalog.items.find(v => v.SKU === li.sku)
        return {
          sku: li.sku,
          qty: li.qty,
          internalIdExportUse: variant?.InternalIDExportUse ?? ''
        }
      })
    const styles = cart.savedStylesArr.filter(style => {
      const model = catalog.modelByStyle(style)
      if (!model) return false
      return model.variants.some(v => {
        const li = cart.lineItems[v.SKU]
        return li && li.qty > 0
      })
    })

    let newOrder
    if (lines.length) {
      newOrder = await ordersStore.addOrder({
        userEmail: auth.user?.email || '',
        status: 'Draft',
        lines,
        styles,
        accountName: accountName.value.trim(),
        shippingAddress: shippingAddress.value.trim(),
        requestedShipDate: requestedShipDate.value,
        poNumber: poNumber.value.trim()
      })
      ordersStore.persist()
    }

    // Clear cart after saving draft
    cart.clearAllSavedStyles()
    for (const sku in cart.lineItems) {
      cart.lineItems[sku].qty = 0
    }
    cart.persist()

    toast.add({ title: 'Draft saved', description: 'Your order was saved as a draft', icon: 'i-lucide-save' })
    trackEvent('Saved Draft', {
        email: auth.user?.email,
        orderId: newOrder?.id || '',        
        total: Number(orderTotalCAD.value),
        currency: currency.value
    })
    router.push('/orders')
  } catch (err) {
    toast.add({ title: 'Draft error', description: 'Could not save draft: ' + err?.message, icon: 'i-lucide-alert-triangle' })
  } finally {
    isPlacingOrder.value = false
  }
}

async function confirmOrder() {
  closeConfirmPopover()
  if (isPlacingOrder.value) return
  if (!auth.isAuthenticated) {
    toast.add({ title: 'Log in to confirm', description: 'Please log in to place your order', color: 'warning' })
    return
  }
  isPlacingOrder.value = true
  try {
    const lines = Object.values(cart.lineItems)
      .filter(li => li.qty > 0)
      .map(li => {
        const variant = catalog.items.find(v => v.SKU === li.sku)
        return {
          sku: li.sku,
          qty: li.qty,
          internalIdExportUse: variant?.InternalIDExportUse ?? ''
        }
      })
    const styles = cart.savedStylesArr.filter(style => {
      const model = catalog.modelByStyle(style)
      if (!model) return false
      return model.variants.some(v => {
        const li = cart.lineItems[v.SKU]
        return li && li.qty > 0
      })
    })

    let newOrder
    if (lines.length) {
      newOrder = await ordersStore.addOrder({
        userEmail: auth.user?.email || '',
        status: 'Submitted',
        lines,
        styles,
        accountName: accountName.value.trim(),
        shippingAddress: shippingAddress.value.trim(),
        requestedShipDate: requestedShipDate.value,
        poNumber: poNumber.value.trim()
      })
      ordersStore.persist()
    }

    if (newOrder) {
      const header = [
        'Account Name',
        'Shipping Address',
        'Requested Ship Date',
        'Sales Order #',
        'PO#',
        'Line ID',
        'Internal ID',
        'Price Level',
        'Quantity',
        'Allocation Strategy'
      ]
      const rows = [header.join(',')]
      for (const line of newOrder.lines) {
        const variant = catalog.items.find(v => v.SKU === line.sku)
        const shipDate = newOrder.requestedShipDate ? formatDateToDDMMYYYY(newOrder.requestedShipDate) : ''
        rows.push([
          escapeCSV(newOrder.accountName || ''),
          escapeCSV(newOrder.shippingAddress || ''),
          shipDate,
          '', // Sales Order #
          escapeCSV(newOrder.poNumber || ''),
          '', // Line ID
          variant?.InternalIDExportUse ?? '', // Internal ID
          '', // Price Level
          line.qty, // Quantity
          '' // Allocation Strategy
        ].join(','))
      }
      const csvContent = rows.join('\r\n')
      let htmlSummary = ''
      for (const style of newOrder.styles) {
        htmlSummary += generateStyleSummaryTable(style, newOrder.lines)
      }
      htmlSummary += generateOrderTotalTable(newOrder.styles, newOrder.lines)
      await $fetch('/api/send-order-mail', {
        method: 'POST',
        body: {
          csvContent,
          orderId: newOrder.id,
          userEmail: auth.user?.email,
          htmlSummary,
          accountName: accountName.value.trim(),
          shippingAddress: shippingAddress.value.trim(),
          requestedShipDate: newOrder.requestedShipDate ? formatDateToDDMMYYYY(newOrder.requestedShipDate) : '',
          poNumber: poNumber.value.trim()
        }
      })
    }
    // Calculate order totals BEFORE clearing cart
    const totalValueCAD = orderTotalCAD.value
    const totalValueOriginal = orderTotal.value
    const originalCurr = currency.value

    trackRevenue(Number(totalValueCAD), 'CAD', auth.user?.email)
    
    trackEvent('Submitted Order', {
        email: auth.user?.email,
        orderId: newOrder?.id || '',        
        total: Number(orderTotal.value),
        currency: originalCurr
    })

    cart.clearAllSavedStyles()
    for (const sku in cart.lineItems) {
      cart.lineItems[sku].qty = 0
    }
    cart.persist()
    

    toast.add({ title: 'Order placed', description: 'Your cart was cleared', icon: 'i-lucide-shirt' })
    router.push('/submitted')
  } catch (err) {
    toast.add({ title: 'Order error', description: 'Could not place order: ' + err?.message, icon: 'i-lucide-alert-triangle' })
  } finally {
    isPlacingOrder.value = false
  }
}

// --- Mail/HTML summary table: now with wider table and SKU column ---
function generateStyleSummaryTable(style, orderLines) {
  const model = catalog.modelByStyle(style)
  if (!model) return ''
  const field = wsFieldForCurrency(currency.value)
  const lines = orderLines.filter(line => {
    const variant = catalog.items.find(v => v.SKU === line.sku)
    return variant && variant.Style === style
  })
  if (!lines.length) return ''
  const rows = lines.map(line => {
    const variant = catalog.items.find(v => v.SKU === line.sku)
    const price = Number(variant?.[field] ?? model?.[field] ?? 0)
    return {
      modelName: model?.name ?? '',
      style: variant?.Style ?? '',
      colour: variant?.Colour ?? '',
      size: variant?.SizeCode ?? variant?.Size ?? '',
      sku: line.sku,
      qty: line.qty,
      totalPrice: price * line.qty
    }
  })
  rows.sort((a, b) => {
    if (a.modelName !== b.modelName) return String(a.modelName).localeCompare(String(b.modelName))
    if (a.style !== b.style) return String(a.style).localeCompare(String(b.style))
    if (a.colour !== b.colour) return String(a.colour).localeCompare(String(b.colour))
    return a.size.localeCompare(b.size)
  })
  const styleTotal = rows.reduce((sum, row) => sum + row.totalPrice, 0)
  let html = `<h3>${model.name} (Style ${model.Style})</h3>\n`
  html += `<table border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse;margin-bottom:16px;width:700px;table-layout:fixed;">`
  html += `<thead>
    <tr>
      <th style="width: 18%;">Color</th>
      <th style="width: 12%;">Size</th>
      <th style="width: 40%;">SKU</th>
      <th style="width: 10%;">Qty</th>
      <th style="width: 20%;">Total</th>
    </tr>
  </thead><tbody>`
  for (const row of rows) {
    html += `<tr>
      <td>${row.colour}</td>
      <td>${row.size}</td>
      <td style="word-break:break-all;">${row.sku}</td>
      <td style="text-align:center;">${row.qty}</td>
      <td style="text-align:right;">${formatNumber(row.totalPrice, currency.value)}</td>
    </tr>`
  }
  html += `</tbody><tfoot>
    <tr>
      <td colspan="4" style="text-align:right;font-weight:bold;">Total for style:</td>
      <td style="text-align:right;font-weight:bold;">${formatNumber(styleTotal, currency.value)}</td>
    </tr>
  </tfoot></table>`
  return html
}
function generateOrderTotalTable(styles, orderLines) {
  let total = 0
  for (const style of styles) {
    const model = catalog.modelByStyle(style)
    if (!model) continue
    const field = wsFieldForCurrency(currency.value)
    const lines = orderLines.filter(line => {
      const variant = catalog.items.find(v => v.SKU === line.sku)
      return variant && variant.Style === style
    })
    for (const line of lines) {
      const variant = catalog.items.find(v => v.SKU === line.sku)
      const price = Number(variant?.[field] ?? model?.[field] ?? 0)
      total += price * line.qty
    }
  }
  return `<table border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse;margin-bottom:16px;width:700px;table-layout:fixed;">
    <tfoot>
      <tr>
        <td colspan="4" style="text-align:right;font-weight:bold;">Total for order:</td>
        <td style="text-align:right;font-weight:bold;">${formatNumber(total, currency.value)}</td>
      </tr>
    </tfoot>
  </table>`
}

// Helper function to escape CSV values
function escapeCSV(value: string): string {
  if (!value) return ''
  // If value contains comma, newline, or quote, wrap in quotes and escape quotes
  if (value.includes(',') || value.includes('\n') || value.includes('"')) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}

// Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
function formatDateToDDMMYYYY(dateStr: string): string {
  if (!dateStr) return ''
  const [year, month, day] = dateStr.split('-')
  return `${day}/${month}/${year}`
}

useContentSeo(page)
</script>

<style scoped>
table {
  table-layout: fixed;
}

th,
td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
